import { useMutation, useQuery } from '@tanstack/react-query'

import type { WorkingBreak, WorkingBreakPayload } from './work-schedules-services-types'
import { workSchedulesServices } from '@/api/work-schedules/work-schedules-services'
import { useProdDate } from '@/hooks/use-query-states'

export const useCurrentDayWorkSchedule = () => {
  const { date, formattedDate } = useProdDate()

  const dayOfWeek = date.getDay()

  return useQuery({
    queryKey: ['work-schedules', formattedDate],
    queryFn: workSchedulesServices.get,
    select: (data) => data?.find((schedule) => schedule.day_of_week === dayOfWeek)
  })
}

export const useWorkSchedules = () => {
  return useQuery({
    queryKey: ['work-schedules'],
    queryFn: workSchedulesServices.get
  })
}

export const useBreaks = () => {
  return useQuery({
    queryKey: ['work-breaks'],
    queryFn: workSchedulesServices.getBreaks
  })
}

type BreakMutationPayload = Partial<WorkingBreakPayload>

type BreakMutationAction = keyof BreakMutationPayload

interface UseBreaksMutationProps {
  workingBreak: WorkingBreak
}

export const useBreaksMutation = ({
  workingBreak
}: UseBreaksMutationProps & { action: BreakMutationAction }) => {
  const updateBreakMutation = useMutation({
    mutationFn: (payload: BreakMutationPayload) =>
      workSchedulesServices.updateBreak(workingBreak.id, payload),
    meta: {
      successMessage: `Break updated successfully`,
      invalidatesQuery: ['orders', 'timeline-orders']
    }
  })

  const addBreakMutation = useMutation({
    mutationFn: (payload: WorkingBreakPayload) => workSchedulesServices.addBreak(payload),
    meta: {
      successMessage: `Break updated successfully`,
      invalidatesQuery: ['orders', 'timeline-orders']
    }
  })

  const handleMutation = (payload: WorkingBreakPayload) => {
    if (workingBreak.id) {
      updateBreakMutation.mutate(payload)
    } else {
      addBreakMutation.mutate(payload)
    }
  }

  return { handleMutation }
}
